/**
 * Discriminator on v2 SPA `PATCH /<resource>/:id` bodies. `routeResolver` dispatches on it, defaulting to `GeneralUpdate`
 * when a body has none. Payload schemas that carry it pin their value with `z.literal(...)`, except
 * `createSubmissionBlockPayloadSchema`, which takes any string (see domain/CLAUDE.md).
 */
export enum ApiPatchOperation {
  AddNewBlockFromLibrary = 'ADD_BLOCK_FROM_LIBRARY',
  AddNewBlocksFromLibrary = 'ADD_BLOCKS_FROM_LIBRARY',
  ApplyEndorsement = 'APPLY_ENDORSEMENT',
  ChangeBaseTemplate = 'CHANGE_BASE_TEMPLATE',
  DeactivateBlock = 'DEACTIVATE_BLOCK',
  PreDraftToDraft = 'PRE_DRAFT_TO_DRAFT',
  CreateResourceBlock = 'CREATE_SUBMISSION_BLOCK',
  CreateResourceSection = 'CREATE_SUBMISSION_SECTION',
  CreateRevision = 'CREATE_REVISION',
  BulkDeleteResourceBlocks = 'BULK_DELETE_SUBMISSION_BLOCKS',
  BulkDeleteSummaryBlocks = 'BULK_DELETE_SUMMARY_BLOCKS',
  DeleteResourceBlock = 'DELETE_SUBMISSION_BLOCK',
  DeleteResourceSection = 'DELETE_SUBMISSION_SECTION',
  ReactivateBlock = 'REACTIVATE_BLOCK',
  RestoreBlock = 'RESTORE_BLOCK',
  RecreatePdfWithAttachment = 'RECREATE_PDF_WITH_ATTACHMENT',
  RemoveResourceAttachment = 'REMOVE_ATTACHMENT',
  RenameResourceAttachment = 'RENAME_ATTACHMENT',
  ReorderResourceBlock = 'REORDER_SUBMISSION_BLOCK',
  ReorderResourceSection = 'REORDER_SUBMISSION_SECTION',
  ResolveCommentsThread = 'RESOLVE_COMMENTS_THREAD',
  ReopenCommentsThread = 'REOPEN_COMMENTS_THREAD',
  RestoreRevision = 'RESTORE_REVISION',
  UpdateResourceAttachmentStatus = 'UPDATE_ATTACHMENT_STATUS',
  UpdateResourceBlock = 'UPDATE_SUBMISSION_BLOCK',
  UpdateResourceData = 'UPDATE_SUBMISSION_DATA',
  UpdateResourceExternalId = 'UPDATE_SUBMISSION_EXTERNAL_ID',
  SyncSubmissionData = 'SYNC_SUBMISSION_DATA',
  UpdateResourceDataItems = 'UPDATE_SUBMISSION_DATA_ITEMS',
  UpdateResourceSection = 'UPDATE_SUBMISSION_SECTION',
  SoftDelete = 'SOFT_DELETE',
  UpdateStatus = 'UPDATE_STATUS',
  UpdateName = 'UPDATE_NAME',
  UpdateContent = 'UPDATE_CONTENT',
  Restore = 'RESTORE',
  /**
   * If no specific operation is in the payload, use this one
   */
  GeneralUpdate = 'GENERAL_UPDATE',
  TeamStatusUpdate = 'TEAM_STATUS_UPDATE',
  CreateSummaryBlock = 'CREATE_SUMMARY_BLOCK',
  UpdateSummaryBlock = 'UPDATE_SUMMARY_BLOCK',
  DeleteSummaryBlock = 'DELETE_SUMMARY_BLOCK',
  DeleteSummarySection = 'DELETE_SUMMARY_SECTION',
  HideBlockFromPreview = 'HIDE_BLOCK_FROM_PREVIEW',
  ShowBlockInPreview = 'SHOW_BLOCK_IN_PREVIEW',
  RestoreBlocksHiddenFromPreview = 'RESTORE_ALL_BLOCKS_HIDDEN_FROM_PREVIEW',
  RestoreEndorsementSummaryTemplate = 'RESTORE_ENDORSEMENT_SUMMARY_TEMPLATE',
  BatchCloneEndorsement = 'BATCH_CLONE_ENDORSEMENT',
  GetBatchCloneEndorsementJobStatus = 'GET_BATCH_CLONE_ENDORSEMENT_JOB_STATUS',
  UserResendInvite = 'USER_RESEND_INVITE',
  UserChangeState = 'USER_CHANGE_STATE',
  UserUpdate = 'USER_UPDATE',
  AddOrRemoveDataSections = 'ADD_OR_REMOVE_DATA_SECTIONS',
  /**
   * Eval config operations — added for the Contract Review Bot evaluation period.
   * Grouped here so they can be cleanly removed together once the eval is done.
   * See plans/eval_configs_increment_a for details.
   */
  UpdateEvalConfigMeta = 'UPDATE_EVAL_CONFIG_META',
  UpdateEvalConfigDraft = 'UPDATE_EVAL_CONFIG_DRAFT',
  PublishEvalConfigDraft = 'PUBLISH_EVAL_CONFIG_DRAFT',
  SetChampionEvalConfig = 'SET_CHAMPION_EVAL_CONFIG',
  CancelEvalRun = 'CANCEL_EVAL_RUN',
  GradeEvalFinding = 'GRADE_EVAL_FINDING',
  ResolveEvalFinding = 'RESOLVE_EVAL_FINDING',
  CommentEvalFinding = 'COMMENT_EVAL_FINDING',
  SendEvalRunFeedback = 'SEND_EVAL_RUN_FEEDBACK',
  StartEvalResolutionCheck = 'START_EVAL_RESOLUTION_CHECK',
  ReorderSummaryBlock = 'REORDER_SUMMARY_BLOCK',
  ReorderSummarySection = 'REORDER_SUMMARY_SECTION',
}

/** A PATCH body without its `operation`. Unlike a plain `Omit`, it won't compile for a type that has no `operation`. */
export type DropApiOperation<T extends { operation: ApiPatchOperation }> = Omit<T, 'operation'>;
